import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import '../../css/Pages.css';
import ScientificCVTemplate from '../../components/Profile/ScientificCV/ScientificCVTemplate';
import { generateWordHTML } from '../../utils/exportWordHelper';
import { wordMsoStyles } from './wordExportStyles';

const sectionOptions = [
    { id: 1, name: '1. Họ và tên' },
    { id: 2, name: '2. Năm sinh' },
    { id: 3, name: '3. Nam/Nữ' },
    { id: 4, name: '4. Học hàm / Học vị' },
    { id: 5, name: '5. Lĩnh vực nghiên cứu' },
    { id: 6, name: '6. Chức danh / Chức vụ' },
    { id: 7, name: '7. Địa chỉ liên hệ' },
    { id: 8, name: '8. Cơ quan công tác' },
    { id: 9, name: '9. Quá trình đào tạo' },
    { id: 10, name: '10. Trình độ ngoại ngữ' },
    { id: 11, name: '11. Quá trình công tác' },
    { id: 12, name: '12. Công trình KH&CN (Bài báo/Sách)' },
    { id: 13, name: '13. Đề tài, dự án KH&CN' },
    { id: 14, name: '14. Giải thưởng' },
    { id: 15, name: '15. Hướng dẫn Sau đại học' },
    { id: 16, name: '16. Lĩnh vực chuyên môn sâu' }
];

const articleTypes = [
    { label: 'Tất cả bài báo (Quốc tế + Trong nước)', value: 'ALL' },
    { label: 'Chỉ bài báo Quốc tế', value: 'INTERNATIONAL' },
    { label: 'Chỉ bài báo Trong nước', value: 'DOMESTIC' }
];

const defaultSections = Array.from({ length: 16 }, (_, i) => i + 1);

const ScientificCV = () => {
    const toast = useRef(null);
    const [cvData, setCvData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDesktop, setIsDesktop] = useState(true);

    const [dialogVisible, setDialogVisible] = useState(false);
    const [tempFilters, setTempFilters] = useState({
        selectedSections: [...defaultSections],
        startYear: null,
        endYear: null,
        articleType: 'ALL'
    });

    const [appliedFilters, setAppliedFilters] = useState({
        selectedSections: [...defaultSections],
        startYear: '',
        endYear: '',
        articleType: 'ALL'
    });

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        alert("Phiên đăng nhập đã hết hạn hoặc bạn không có quyền truy cập. Vui lòng đăng nhập lại!");
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
    }, []);

    const fetchCVData = useCallback(async () => {
        setIsLoading(true);
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Chưa đăng nhập!' });
                handleLogout();
                return;
            }
            const currentUser = JSON.parse(userStr);
            const urlParams = new URLSearchParams(window.location.search);
            const queryId = urlParams.get('id');
            const targetUserId = queryId ? parseInt(queryId) : currentUser.Id;

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificcv?userId=${targetUserId}`, {
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setCvData(result);
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu Lý lịch!' });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchCVData();
    }, [fetchCVData]);

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadWord = () => {
        const cvElement = document.querySelector('.printable-cv');
        if (!cvElement) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy nội dung để tải' });
            return;
        }

        let rawHtml = cvElement.innerHTML;

        rawHtml = rawHtml.replace(
            /<div class="word-section-break-placeholder[^>]*><\/div>/ig,
            '<br clear="all" style="page-break-before:always; mso-break-type:section-break" />'
        );

        let htmlContent = generateWordHTML(rawHtml);

        if (htmlContent.includes('</head>')) {
            htmlContent = htmlContent.replace('</head>', wordMsoStyles + '</head>');
        } else {
            htmlContent = wordMsoStyles + htmlContent;
        }

        const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const fullNameStr = cvData?.User ? `${cvData.User.LastName || ''} ${cvData.User.FirstName || ''}`.trim() : '';
        const fileName = fullNameStr ? `LLKH_${fullNameStr.replace(/\s+/g, '_')}.doc` : 'LyLichKhoaHoc.doc';

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const applyCurrentFilters = () => {
        setAppliedFilters({
            selectedSections: tempFilters.selectedSections,
            startYear: tempFilters.startYear ? tempFilters.startYear.getFullYear().toString() : '',
            endYear: tempFilters.endYear ? tempFilters.endYear.getFullYear().toString() : '',
            articleType: tempFilters.articleType
        });
    };

    const handleApplyOnly = () => {
        applyCurrentFilters();
        setDialogVisible(false);
        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã cập nhật hiển thị lý lịch!' });
    };

    const handleApplyAndDownload = () => {
        applyCurrentFilters();
        setDialogVisible(false);
        setTimeout(() => {
            handleDownloadWord();
        }, 300);
    };

    const onSectionChange = (secId) => {
        let selected = [...tempFilters.selectedSections];
        if (selected.includes(secId)) {
            selected = selected.filter(val => val !== secId);
        } else {
            selected.push(secId);
        }
        setTempFilters({ ...tempFilters, selectedSections: selected });
    };

    const handleSelectAll = (checked) => {
        setTempFilters({
            ...tempFilters,
            selectedSections: checked ? [...defaultSections] : []
        });
    };

    const renderFooter = () => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px' }}>
            <button
                className="btn-add-new"
                style={{ backgroundColor: '#95a5a6', border: 'none', color: '#fff', borderRadius: '4px', padding: '10px 20px', cursor: 'pointer', fontSize: '15px' }}
                onClick={() => setDialogVisible(false)}
            >
                Đóng
            </button>
            <button
                className="btn-add-new"
                style={{ backgroundColor: '#34495e', border: 'none', color: '#fff', borderRadius: '4px', padding: '10px 20px', cursor: 'pointer', fontSize: '15px' }}
                onClick={handleApplyOnly}
            >
                <i className="fa-solid fa-eye" style={{ marginRight: '6px' }}></i> Áp dụng xem
            </button>
            <button
                className="btn-add-new"
                style={{ backgroundColor: '#2980b9', border: 'none', color: '#fff', borderRadius: '4px', padding: '10px 20px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold' }}
                onClick={handleApplyAndDownload}
            >
                <i className="fa-solid fa-download" style={{ marginRight: '6px' }}></i> Áp dụng & Tải
            </button>
        </div>
    );

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" />

            <div
                className="no-print page-header"
                style={isDesktop
                    ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }
                    : { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }
                }
            >
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase', ...(isDesktop ? {} : { margin: 0 }) }}>LÝ LỊCH KHOA HỌC</h2>
                </div>

                <div style={{ display: 'flex', gap: '10px', ...(isDesktop ? {} : { width: '100%', flexDirection: 'column' }) }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#8e44ad', ...(isDesktop ? {} : { width: '100%', margin: 0 }) }}
                        onClick={() => setDialogVisible(true)}
                        disabled={isLoading}
                    >
                        <i className="fa-solid fa-sliders" style={{ marginRight: '8px' }}></i> Tùy chỉnh tải lý lịch
                    </button>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#2980b9', ...(isDesktop ? {} : { width: '100%', margin: 0 }) }}
                        onClick={handleDownloadWord}
                        disabled={isLoading}
                    >
                        <i className="fa-solid fa-file-word" style={{ marginRight: '8px' }}></i> Tải lý lịch
                    </button>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#27ae60', ...(isDesktop ? {} : { width: '100%', margin: 0 }) }}
                        onClick={handlePrint}
                        disabled={isLoading}
                    >
                        <i className="fa-solid fa-print" style={{ marginRight: '8px' }}></i> In lý lịch
                    </button>
                </div>
            </div>

            <Dialog
                header={<span style={{ fontSize: '20px', fontWeight: 'bold', color: '#2c3e50' }}>Tùy chỉnh nội dung xuất Lý lịch khoa học</span>}
                visible={dialogVisible}
                style={{ width: '85vw', maxWidth: '1200px' }}
                breakpoints={{ '1200px': '90vw', '960px': '95vw', '641px': '100vw' }}
                onHide={() => setDialogVisible(false)}
                footer={renderFooter()}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', padding: '16px 8px' }}>

                    <div style={{ flex: '1 1 50%', minWidth: '380px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <span style={{ fontWeight: '600', color: '#2c3e50', fontSize: '16px' }}>Các mục xuất ra file</span>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => handleSelectAll(true)}
                                    style={{ fontSize: '13px', background: '#ecf0f1', border: '1px solid #bdc3c7', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                    Chọn tất cả
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleSelectAll(false)}
                                    style={{ fontSize: '13px', background: '#ecf0f1', border: '1px solid #bdc3c7', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                    Bỏ chọn
                                </button>
                            </div>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                            gap: '12px',
                            maxHeight: '480px',
                            overflowY: 'auto',
                            paddingRight: '10px'
                        }}>
                            {sectionOptions.map(sec => {
                                const isChecked = tempFilters.selectedSections.includes(sec.id);
                                return (
                                    <div
                                        key={sec.id}
                                        onClick={() => onSectionChange(sec.id)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px 16px',
                                            borderRadius: '8px',
                                            border: isChecked ? '1px solid #3498db' : '1px solid #e0e0e0',
                                            background: isChecked ? '#ebf5fb' : '#fafafa',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <Checkbox
                                            inputId={`sec${sec.id}`}
                                            value={sec.id}
                                            checked={isChecked}
                                            onChange={() => onSectionChange(sec.id)}
                                        />
                                        <label
                                            htmlFor={`sec${sec.id}`}
                                            style={{ marginLeft: '12px', fontSize: '14.5px', cursor: 'pointer', userSelect: 'none', color: isChecked ? '#2980b9' : '#333', fontWeight: isChecked ? '500' : 'normal' }}
                                        >
                                            {sec.name}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div style={{ flex: '1 1 40%', minWidth: '350px', background: '#fcfcfc', border: '1px solid #edf0f2', borderRadius: '8px', padding: '24px' }}>
                        <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '24px' }}>
                            <span style={{ fontWeight: '600', color: '#2c3e50', fontSize: '16px' }}>Bộ lọc riêng Mục 12 (Công trình)</span>
                            {!tempFilters.selectedSections.includes(12) && (
                                <p style={{ margin: '6px 0 0 0', fontSize: '13.5px', color: '#e67e22' }}>
                                    (Lưu ý: Mục 12 đang bị bỏ chọn nên sẽ không xuất hiện trong file)
                                </p>
                            )}
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', fontSize: '14.5px', fontWeight: '600', marginBottom: '8px', color: '#34495e' }}>
                                Phân loại bài báo
                            </label>
                            <Dropdown
                                value={tempFilters.articleType}
                                options={articleTypes}
                                onChange={(e) => setTempFilters({ ...tempFilters, articleType: e.value })}
                                appendTo="self"
                                style={{ width: '100%', height: '46px', display: 'flex', alignItems: 'center', fontSize: '15px' }}
                                valueTemplate={(option) => (
                                    <div style={{ paddingLeft: '6px' }}>
                                        {option ? option.label : 'Chọn phân loại'}
                                    </div>
                                )}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', fontSize: '14.5px', fontWeight: '600', marginBottom: '8px', color: '#34495e' }}>
                                Từ năm
                            </label>
                            <Calendar
                                value={tempFilters.startYear}
                                onChange={(e) => setTempFilters({ ...tempFilters, startYear: e.value })}
                                view="year"
                                dateFormat="yy"
                                showIcon
                                appendTo="self"
                                style={{ width: '100%', height: '46px' }}
                                inputStyle={{ fontSize: '15px', padding: '10px 12px' }}
                                placeholder="Chọn năm bắt đầu"
                                showButtonBar
                            />
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', fontSize: '14.5px', fontWeight: '600', marginBottom: '8px', color: '#34495e' }}>
                                Đến năm
                            </label>
                            <Calendar
                                value={tempFilters.endYear}
                                onChange={(e) => setTempFilters({ ...tempFilters, endYear: e.value })}
                                view="year"
                                dateFormat="yy"
                                showIcon
                                appendTo="self"
                                style={{ width: '100%', height: '46px' }}
                                inputStyle={{ fontSize: '15px', padding: '10px 12px' }}
                                placeholder="Chọn năm kết thúc"
                                showButtonBar
                            />
                        </div>
                    </div>
                </div>
            </Dialog>

            <div className="cv-wrapper">
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <i className="fa-solid fa-circle-notch fa-spin fa-2x" style={{ color: '#3498db' }}></i>
                        <p style={{ marginTop: '10px', color: '#666' }}>Đang tổng hợp dữ liệu</p>
                    </div>
                ) : cvData && cvData.User ? (
                    <ScientificCVTemplate data={cvData} filters={appliedFilters} />
                ) : (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#e74c3c' }}>
                        <h3>Không tìm thấy dữ liệu người dùng</h3>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScientificCV;